/**
 * City hub `/{cc}/{lang}/city/:city` (web plan W3.2), born at its final URL:
 * `/pk/en/city/lahore`. One slug per city: which slugs exist is the backend's
 * `cityPage` (B3.7b) answer.
 *
 * Everything comes from the backend through `useCityPage`; the SEO server
 * builds the head from the same query (server/routes.mjs), so the raw HTML
 * and the page agree:
 * - H1 / title / description from the city formula (`cityPageHead`):
 *   "Sale on Brands in {City}" (always); "Today" in the title while a listed
 *   sale is live;
 * - the live sales, LIST_PAGE_SIZE a page with crawlable `?page=N` links (a
 *   page that does not exist says so and is `noindex`; `?page=1` → 301 to the
 *   bare path);
 * - on page 1: upcoming sales, brands A–Z with store counts (each linking to
 *   its brand × city page when that pair is indexable, else to the brand hub;
 *   D-5: a brand hub is linked only while `seo.indexable`), categories, nearby
 *   cities (indexable only);
 * - D-5: a nearby city hub is linked only while its own `seo.indexable`.
 *
 * An unknown city or bad slug is the 404 page (noindex); another spelling of
 * the hub's path (an old slug, a merged city's alias, a trailing slash) is
 * replaced by its `urlPath`. Every string comes from the catalogue (`city:page.*`).
 */
import { lazy, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { MapPin, Tag } from 'lucide-react';
import { PromotionCard } from '@/components/cards';
import { CardSkeleton, ErrorState, NoPageNotice, Pagination } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useCityPage } from '@/hooks/useCityPage';
import { useImpressionRef } from '@/hooks/useImpressionTracking';
import { PageHead } from '@/components/seo/PageHead';
import { formatNumber } from '@/i18n/format';
import { useTranslate } from '@/i18n/LocaleLanguage';
import type { Translate } from '@/i18n';
import {
  cityHubKey,
  cityHubLastPage,
  cityHubName,
  cityHubPromotionsOn,
  linkedFromHubs,
  sitePath,
  type CityHubPage,
} from '@/seo/cityHubs';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { cityPageHead } from '@/seo/pageHeads';
import { entityPath, parseListPage } from '@/seo/urls';
import { cleanName } from '@/seo/text';

// Lazy like every page in App.tsx, so the 404 page stays in its own chunk.
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** A grid cell that registers the card for impression view-tracking. */
function TrackedCell({ id, children }: { id: string; children: ReactNode }) {
  const impressionRef = useImpressionRef('promotion', id);
  return (
    <li ref={impressionRef} className="h-full *:h-full">
      {children}
    </li>
  );
}

const GRID = 'mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
const COUNT = 'text-base font-semibold text-ink-tertiary';
const SECTION_HEADING = 'text-xl font-extrabold tracking-tight text-ink';

/** The page's `?page=` against the hub's live sales. */
function hubPageOf(search: string, hub: CityHubPage): { page: number; ok: boolean; requested: string } {
  const param = parseListPage(search);
  const requested = param.raw ?? '';
  if (!param.valid) return { page: 1, ok: false, requested };
  return { page: param.page, ok: param.page <= cityHubLastPage(hub), requested };
}

function paginationLabels(t: Translate) {
  return {
    previous: t('common:pagination.previous'),
    next: t('common:pagination.next'),
    page: (page: number) => t('common:pagination.page', { page }),
  };
}

export interface CityHubContentProps {
  hub: CityHubPage;
  locale?: Locale;
}

/** The hub for one city's answer. Exported so tests can render any fixture. */
export function CityHubContent({ hub, locale = DEFAULT_LOCALE }: CityHubContentProps) {
  const { search } = useLocation();
  const t = useTranslate();
  const { page, ok, requested } = hubPageOf(search, hub);
  const head = cityPageHead(hub, { page, locale, t });
  const city = cityHubName(hub.city);
  const live = cityHubPromotionsOn(hub, page);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      {/* A page past the end or a malformed `?page=` does not exist (the server's 404). */}
      <PageHead head={head} noindex={!ok} />
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{head.h1}</h1>
      </header>

      {!ok ? (
        <NoPageNotice
          basePath={hub.urlPath}
          requested={requested}
          labels={{
            noPage: (shown) => t('common:pagination.noPage', { page: shown }),
            firstPage: t('common:pagination.firstPage'),
          }}
        />
      ) : (
        <div className="mt-8 space-y-12">
          <section aria-labelledby="city-live-heading">
            <h2 id="city-live-heading" className={SECTION_HEADING}>
              {t('city:page.live', { city })}
              {hub.livePromotions.length > 0 && (
                <span className={COUNT}>{` (${formatNumber(hub.livePromotions.length, locale)})`}</span>
              )}
            </h2>
            {live.length > 0 ? (
              <ul className={GRID}>
                {live.map((promotion) => (
                  <TrackedCell key={promotion.id} id={promotion.id}>
                    <PromotionCard promotion={promotion} variant="grid" />
                  </TrackedCell>
                ))}
              </ul>
            ) : (
              <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line px-6 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Tag size={28} className="text-primary" aria-hidden="true" />
                </span>
                <p className="font-bold text-ink">{t('city:page.liveEmpty', { city })}</p>
                <p className="text-sm text-ink-secondary">{t('city:page.liveEmptyHint')}</p>
              </div>
            )}
            {/* Plain <a href> links to every page of live sales, for crawlers. */}
            <Pagination
              basePath={hub.urlPath}
              page={page}
              lastPage={cityHubLastPage(hub)}
              label={t('city:page.pages')}
              labels={paginationLabels(t)}
            />
          </section>

          {page === 1 && hub.upcomingPromotions.length > 0 && (
            <section aria-labelledby="city-upcoming-heading">
              <h2 id="city-upcoming-heading" className={SECTION_HEADING}>
                {t('city:page.upcoming', { city })}
                <span className={COUNT}>{` (${formatNumber(hub.upcomingPromotions.length, locale)})`}</span>
              </h2>
              <ul className={GRID}>
                {hub.upcomingPromotions.map((promotion) => (
                  <TrackedCell key={promotion.id} id={promotion.id}>
                    <PromotionCard promotion={promotion} variant="grid" />
                  </TrackedCell>
                ))}
              </ul>
            </section>
          )}

          {page === 1 && hub.brandCount > 0 && hub.brands.length > 0 && (
            <section aria-labelledby="city-brands-heading">
              <h2 id="city-brands-heading" className={SECTION_HEADING}>
                {t('city:page.brands', {
                  city,
                  count: formatNumber(hub.brandCount, locale),
                })}
              </h2>
              <ul className="mt-4 flex flex-wrap gap-2">
                {hub.brands.map(({ brand, storeCount, brandCityPath }) => {
                  const label = cleanName(brand.name) || brand.name;
                  // D-5: link the brand × city page when indexable, else the
                  // brand hub when it is indexable, else plain text.
                  const brandCityTo = brandCityPath ? sitePath(brandCityPath) : null;
                  const brandHubPath = entityPath(brand, 'brand');
                  const to = brandCityTo
                    ? brandCityTo
                    : linkedFromHubs(brand) && brandHubPath
                      ? brandHubPath
                      : null;
                  return (
                    <li key={brand.id}>
                      {to ? (
                        <Link
                          to={to}
                          className="inline-flex min-h-10 items-center rounded-full border border-line px-3 py-1.5 text-sm font-semibold text-ink transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary-strong"
                        >
                          {label}
                          <span className="ml-1.5 text-xs font-normal text-ink-tertiary">{storeCount}</span>
                        </Link>
                      ) : (
                        <span className="inline-flex min-h-10 items-center rounded-full border border-line-light px-3 py-1.5 text-sm text-ink-secondary">
                          {label}
                          <span className="ml-1.5 text-xs font-normal text-ink-tertiary">{storeCount}</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {page === 1 && hub.categories.length > 0 && (
            <section aria-labelledby="city-categories-heading">
              <h2 id="city-categories-heading" className={SECTION_HEADING}>
                {t('city:page.categories', { city })}
              </h2>
              {/* The backend lists only indexable category hubs (D-5). */}
              <ul className="mt-4 flex flex-wrap gap-2">
                {hub.categories.map(({ category, brandCount: catBrands }) => {
                  const catPath = sitePath(category.urlPath);
                  const catLabel = cleanName(category.seoLabel) || cleanName(category.title) || category.title;
                  return (
                    <li key={category.id}>
                      {catPath ? (
                        <Link
                          to={catPath}
                          className="inline-flex min-h-10 items-center rounded-full border border-primary/40 bg-primary/15 px-3 py-1.5 text-sm font-semibold text-primary-strong transition-colors hover:bg-primary/25"
                        >
                          {catLabel}
                          <span className="ml-1.5 text-xs font-normal opacity-70">{catBrands}</span>
                        </Link>
                      ) : (
                        <span className="inline-flex min-h-10 items-center rounded-full border border-line-light px-3 py-1.5 text-sm text-ink-secondary">
                          {catLabel}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {page === 1 && hub.nearbyCities.length > 0 && (
            <section aria-labelledby="city-nearby-heading">
              <h2 id="city-nearby-heading" className={SECTION_HEADING}>
                {t('city:page.nearby')}
              </h2>
              {/* The backend lists only indexable nearby city hubs (D-5). */}
              <ul className="mt-4 flex flex-wrap gap-2">
                {hub.nearbyCities.map((nearbyCity) => {
                  const nearbyPath = sitePath(nearbyCity.urlPath);
                  const nearbyName = cityHubName(nearbyCity);
                  return (
                    <li key={nearbyCity.slug}>
                      {nearbyPath && linkedFromHubs(nearbyCity) ? (
                        <Link
                          to={nearbyPath}
                          className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-sm font-semibold text-ink transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary-strong"
                        >
                          <MapPin size={14} className="text-ink-tertiary" aria-hidden="true" />
                          {nearbyName}
                        </Link>
                      ) : (
                        <span className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-line-light px-3 py-1.5 text-sm text-ink-secondary">
                          <MapPin size={14} aria-hidden="true" />
                          {nearbyName}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/** Loading or a failed load: say what the page is and leave robots alone (web plan W0.2). */
function CityHubPending({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  const t = useTranslate();
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead title={t('city:page.loading')} keepRobots />
      {error ? (
        <ErrorState
          title={t('city:page.error')}
          titleAs="h1"
          message={error}
          onRetry={onRetry}
          retryLabel={t('city:page.retry')}
        />
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface CityPageProps {
  /** The route's locale: routes are generated per locale with literal segments (App.tsx). */
  locale?: Locale;
}

export default function CityPage({ locale = DEFAULT_LOCALE }: CityPageProps) {
  const { city } = useParams();
  const { pathname, search, hash } = useLocation();
  const key = cityHubKey(city);
  const { hub, status, error, refetch } = useCityPage(key, locale);

  if (status === 'not_found') return <NotFoundPage />;
  if (status !== 'ok' || !hub) return <CityHubPending error={status === 'error' ? error : null} onRetry={refetch} />;

  // Another spelling of the hub's path (old slug, merged city's alias): the
  // page itself at its one URL (the SEO server answers it with one 301).
  const canonical = sitePath(hub.urlPath);
  if (canonical && pathname !== canonical) return <Navigate replace to={{ pathname: canonical, search, hash }} />;

  return <CityHubContent key={hub.urlPath} hub={hub} locale={locale} />;
}
